export const useCategoryStore = defineStore('category', () => {
  const { categoryRecommended } = useRuntimeConfig().public
  const store = useStore()
  const { filterLoading } = storeToRefs(store)
  const category = ref()
  const baseCategory = ref()
  const productsData = ref()
  const recommended = ref()
  const newProducts = ref()
  const blog = ref()
  const scrollLoading = ref()
  const url = ref<any>({})
  const scrollIndex = ref(0)
  const startPage = ref(1)
  const min = ref<any>(null)
  const max = ref<any>(null)
  const open = ref<number[]>([])
  const checked = ref<string[]>([])
  const allProperties = ref<any[]>([]) 
  const id = computed(() => category.value.id)
  const name = computed(() => {
    const page = productsData.value.page
    const name = category.value.customFields.category_customH1 || category.value.name
    return page > 1 ? `${name} - strona ${page}` : name
  })
  const description = computed(() => category.value.description)
  const descriptionFooter = computed(() => category.value.customFields.category_categoryFooterDescription)
  const media = computed(() => category.value.media)
  const breadcrumbs = computed(() => category.value.extensions.breadcrumbWithUrl.breadcrumbs)
  const subcategoryTypeField = computed(() => category.value.customFields.category_subcategoryTypeField)
  const category_categoryFooterDescription = computed(() => category.value.customFields.category_categoryFooterDescription)
  const category_categoryFooterImage = computed(() => category.value.extensions.mediaExtension ? category.value.extensions.mediaExtension.categoryFooterImage[0] : null)
  const category_youtubeId = computed(() => category.value.customFields.category_youtubeId)
  const category_categoryFooterMap = computed(() => category.value.customFields.category_categoryFooterMap)
  const category_categoryHeaderTable = computed(() => category.value.customFields.category_categoryHeaderTable)
  const category_subcategoryH2 = computed(() => category.value.customFields.category_subcategoryH2)
  const availableSortings = computed(() => productsData.value.availableSortings)
  const priceMin = computed(() => Math.floor(productsData.value.aggregations.price.min))
  const priceMax = computed(() => Math.ceil(productsData.value.aggregations.price.max))
  const properties = computed(() => productsData.value.aggregations.properties.entities)
  const metaTitle = computed(() => {
    const page = scrollIndex.value ? startPage.value : productsData.value.page
    const metaTitle = category.value.metaTitle || category.value.name
    return page > 1 ? `${metaTitle} - strona ${page}` : metaTitle
  })
  const metaDescription = computed(() => {
    const page = scrollIndex.value ? startPage.value : productsData.value.page
    return page > 1 ? `${category.value.metaDescription} - strona ${page}` : category.value.metaDescription
  })
  const init = async ({ id, query, url }: { id: string, query?: any, url: string }) => {
    const getCategory = await useFetch(`/store-api/url/${id}`, {
      method: 'POST',
      body: {
        includeCategoryFooterMediaExtension: true,
        includes: {
          category: ['id', 'name', 'active', 'description', 'mediaExtension', 'media', 'customFields', 'breadcrumbWithUrl', 'metaTitle', 'metaDescription']
        }
      },
      query: {
        url
      }
    })
    category.value = getCategory.data.value
    const showRecommended = computed(() => categoryRecommended && ['/k/okazje', '/k/oferta', '/k/jazda'].includes(url))
    const [products, recommendedData, baseCategoryData, newProductsData, blogData] = await Promise.all([
      useFetch('/product-listing', {
        method: 'POST',
        body: {
          id: category.value.id,
          queryData: query,
          url,
          displayBaseProducts: category.value.customFields.category_displayBaseProducts,
          showBaseProducts: category.value.customFields.category_showBaseProducts
        },
        query: {
          url
        }
      }),
      showRecommended.value ? useFetch('/product-listing', {
        method: 'POST',
        body: {
          id: categoryRecommended,
          limit: 4,
          url,
          getShowBaseProducts: true
        },
        query: {
          url
        }
      }) : null,
      useFetch(`/store-api/navigation/${category.value.id}/${category.value.id}`, {
        method: 'POST',
        body: {
          filter: [
            {
              type: 'not',
              queries: [
                {
                  field: 'category.customFields.category_isBaseCategory',
                  type: 'equals',
                  value: 'true'
                }
              ]
            }
          ],
          includes: {
            category: ['id', 'name', 'media', 'categoryUrlData', 'customFields.category_categoryFooterMap']
          }
        },
        query: {
          url
        }
      }),
      useFetch('/store-api/product', {
        method: 'POST',
        body: {
          limit: 2,
          sort: [
            {
              field: 'createdAt',
              order: 'desc'
            }
          ],
          filter: [
            {
              type: 'equals',
              field: 'product.parentId',
              value: null
            },
            {
              type: 'not',
              operator: 'or',
              queries: [
                {
                  type: 'equals',
                  field: 'seoUrls.id',
                  value: null
                },
                {
                  type: 'equals',
                  field: 'product.id',
                  value: 'b02ec300f88363cac4572b8084bad604'
                }
              ]
            }
          ],
          associations: {
            media: {
              sort: [
                {
                  field: 'position'
                }
              ]
            },
            productReviews: {}
          },
          includes: {
            product: ['id', 'name', 'media', 'calculatedPrice', 'calculatedPrices', 'ratingAverage', 'productReviews', 'seoUrls', 'urlPrefix', 'customFields.product_city']
          }
        },
        query: {
          url
        }
      }),
      useFetch('/blog-api/posts', {
        method: 'POST',
        body: {
          name: category.value.name
        },
        query: {
          url
        }
      })
    ])
    productsData.value = products.data.value
    
    if (products.data.value.aggregations.properties.entities) {
      allProperties.value = JSON.parse(JSON.stringify(products.data.value.aggregations.properties.entities))
    }

    recommended.value = recommendedData?.data.value
    baseCategory.value = baseCategoryData.data.value
    newProducts.value = newProductsData.data.value.elements
    blog.value = blogData.data.value
  }
  const search = async ({ query, url }: { query: any, url: string }) => {
    const [searchData, newProductsData] = await Promise.all([
      useFetch('/search', {
        method: 'POST',
        body: {
          query,
          url
        },
        query: {
          url
        }
      }),
      useFetch('/store-api/product', {
        method: 'POST',
        body: {
          limit: 2,
          sort: [
            {
              field: 'createdAt',
              order: 'desc'
            }
          ],
          filter: [
            {
              type: 'equals',
              field: 'product.parentId',
              value: null
            },
            {
              type: 'not',
              operator: 'or',
              queries: [
                {
                  type: 'equals',
                  field: 'seoUrls.id',
                  value: null
                },
                {
                  type: 'equals',
                  field: 'product.id',
                  value: 'b02ec300f88363cac4572b8084bad604'
                }
              ]
            }
          ],
          associations: {
            media: {
              sort: [
                {
                  field: 'position'
                }
              ]
            },
            productReviews: {}
          },
          includes: {
            product: ['id', 'name', 'media', 'calculatedPrice', 'calculatedPrices', 'ratingAverage', 'productReviews', 'seoUrls', 'urlPrefix', 'customFields.product_city']
          }
        },
        query: {
          url
        }
      })
    ])
    productsData.value = searchData.data.value
    newProducts.value = newProductsData.data.value.elements
  }
  const searchLoad = async ({ query, more, url }: { query: any, more?: boolean, url: string }) => {
    const searchData = await $fetch('/search', {
      method: 'POST',
      body: {
        query,
        url
      },
      query: {
        url
      }
    })
    if (more) {
      productsData.value.elements = productsData.value.elements.concat(searchData.elements)
    } else {
      productsData.value = searchData
    }
  }
  const loadFilter = async ({ query, more, url, displayProducts, search: searchData }: { query: any, more: boolean, url: string, displayProducts?: boolean, search?: boolean }) => {
    if (searchData) {
      await searchLoad({ query, more, url })
    } else {
      const products = await $fetch('/product-listing', {
        method: 'POST',
        body: {
          id: category.value.id,
          queryData: query,
          url,
          displayBaseProducts: category.value.customFields.category_displayBaseProducts && !displayProducts
        },
        query: { url }
      })

      if (more) {
        productsData.value.elements = productsData.value.elements.concat(products.elements)
      } else {
        productsData.value = products

        if (products.aggregations?.properties?.entities && allProperties.value.length > 0) {
          const mergedProperties = allProperties.value.map(originalFilter => {
            const updatedFilter = products.aggregations.properties.entities.find((p: any) => p.id === originalFilter.id)

            if (!updatedFilter) {
              return {
                ...originalFilter,
                options: originalFilter.options.map((opt: any) => ({ ...opt, count: 0 }))
              }
            }

            const mergedOptions = originalFilter.options.map((originalOpt: any) => {
              const updatedOpt = updatedFilter.options.find((o: any) => o.id === originalOpt.id)
              return updatedOpt ? updatedOpt : { ...originalOpt, count: 0 }
            })

            return { ...originalFilter, options: mergedOptions }
          })

          productsData.value.aggregations.properties.entities = mergedProperties
        }
      }
    }
  }

  const openFilter = (id: number) => {
    if (!open.value.includes(id)) {
      open.value.push(id)
    }
  }
  const getChecked = ({ query }: { query: any }) => {
    const propertiesData: any = query.properties
    if (propertiesData) {
      checked.value = propertiesData.split('|')
    }
    const minPrice = query['min-price']
    if (minPrice) {
      min.value = minPrice
    }
    const maxPrice = query['max-price']
    if (maxPrice) {
      max.value = maxPrice
    }
  }

  const load = async ({ type, p, key, mobile, path, search }: { type: any, p?: number, key?: string, mobile?: boolean, path: string, search?: boolean }) => {
    url.value.q = useRoute().query.q
    url.value.p = undefined
    if (type === 'order') {
      url.value.order = key
    } else if (type === 'p') {
      url.value.p = p
    } else if (type === 'min-price') {
      url.value['min-price'] = min.value
    } else if (type === 'max-price') {
      url.value['max-price'] = max.value
    } else if (type === 'properties') {
      url.value.properties = checked.value.length !== 0 ? checked.value.join('|') : undefined
    } else if (type === 'remove') {
      min.value = null
      max.value = null
      checked.value = []
      url.value = {
        'min-price': undefined,
        'max-price': undefined,
        properties: undefined,
        order: url.value.order,
        p: undefined,
        q: url.value.q
      }
    }
    filterLoading.value = true
    await loadFilter({ query: url.value, more: false, url: path, displayProducts: url.value.properties ? true : false, search })
    await navigateTo({ path, query: url.value })
    scrollIndex.value = 0
    startPage.value = 1
    filterLoading.value = false
    if (mobile) {
      store.getModal(false, null)
    }
  }

  return { category, productsData, recommended, baseCategory, newProducts, blog, scrollLoading, url, scrollIndex, startPage, min, max, open, checked, allProperties, id, name, description, descriptionFooter, media, breadcrumbs, subcategoryTypeField, category_categoryFooterDescription, category_categoryFooterImage, category_youtubeId, category_categoryFooterMap, category_categoryHeaderTable, category_subcategoryH2, priceMin, priceMax, properties, availableSortings, metaTitle, metaDescription, init, search, loadFilter, openFilter, getChecked, load }
})
